/**
 * src/types.ts — TypeScript types for the whole app.
 *
 * Validated shapes are DERIVED from the Zod schemas in `schema.ts` via `z.infer`
 * (never hand-duplicated, per §8.2). Pure engine-output shapes that don't cross a
 * trust boundary are plain interfaces here. Also hosts the generic tax defaults and
 * `defaultScenarioInputs()` factory (§6.20, §8).
 */
import type { z } from 'zod';
import type {
  assetClassSchema,
  collateralBucketSchema,
  complexityModeSchema,
  dividendHandlingSchema,
  drawFrequencySchema,
  liquidationPolicySchema,
  capGainsBracketRowSchema,
  capGainsBracketTableSchema,
  filingStatusSchema,
  monteCarloConfigSchema,
  paymentModeSchema,
  ratePathSchema,
  ratePathTypeSchema,
  scenarioInputsSchema,
  scenarioSchema,
  scheduledAmountSchema,
  sofrObservationSchema,
  stressPresetSchema,
  taxFieldsSchema,
  themeSchema,
  uiStateSchema,
} from './schema';

// ───────────── inferred from Zod (single source of truth) ─────────────

export type SofrObservation = z.infer<typeof sofrObservationSchema>;
export type FilingStatus = z.infer<typeof filingStatusSchema>;
export type CapGainsBracketRow = z.infer<typeof capGainsBracketRowSchema>;
export type CapGainsBracketTable = z.infer<typeof capGainsBracketTableSchema> & {
  statuses: Record<FilingStatus, CapGainsBracketRow>;
};
export type PaymentMode = z.infer<typeof paymentModeSchema>;
export type RatePathType = z.infer<typeof ratePathTypeSchema>;
export type StressPreset = z.infer<typeof stressPresetSchema>;
export type DrawFrequency = z.infer<typeof drawFrequencySchema>;
export type DividendHandling = z.infer<typeof dividendHandlingSchema>;
export type AssetClass = z.infer<typeof assetClassSchema>;
export type LiquidationPolicy = z.infer<typeof liquidationPolicySchema>;
export type Theme = z.infer<typeof themeSchema>;
export type ComplexityMode = z.infer<typeof complexityModeSchema>;

export type CollateralBucket = z.infer<typeof collateralBucketSchema>;
export type RatePath = z.infer<typeof ratePathSchema>;
export type ScheduledAmount = z.infer<typeof scheduledAmountSchema>;
export type TaxFields = z.infer<typeof taxFieldsSchema>;
export type TaxProfile = TaxFields;
export type MonteCarloConfig = z.infer<typeof monteCarloConfigSchema>;
export type ScenarioInputs = z.infer<typeof scenarioInputsSchema>;
export type Scenario = z.infer<typeof scenarioSchema>;
export type UiState = z.infer<typeof uiStateSchema>;

// ───────────── pure engine outputs (not persisted, not trust-boundary) ─────────────

export interface MonthlyRow {
  monthIndex: number;
  date: string;
  /** The benchmark this month; SOFR only pre-fills it (§7.1). */
  baseRate: number;
  apr: number;
  afterTaxApr: number;
  drawThisMonth: number;
  paymentThisMonth: number;
  interestThisMonth: number;
  startBalance: number;
  endBalance: number;
  portfolioValue: number;
  basis: number;
  gainFraction: number;
  deployedProceeds: number;
  netWorth: number;
  loanValueOfCollateral: number;
  ltv: number;
  marginOfSafety: number;
  callCushionPct: number;
  maintenanceCall: boolean;
  forcedSale: number;
  realizedTaxThisMonth: number;
  cumulativeInterest: number;
  cumulativeDraws: number;
  cumulativePayments: number;
  cumulativeTax: number;
}

export interface ProjectionSummary {
  effectiveSpread: number;
  startApr: number;
  endApr: number;
  afterTaxApr: number;
  endingBalance: number;
  endingPortfolio: number;
  endingNetWorth: number;
  totalInterest: number;
  totalDraws: number;
  totalPayments: number;
  totalRealizedTax: number;
  worstLtv: number;
  minCallCushionPct: number;
  firstMaintenanceCallMonth: number | null;
  breakEvenReturnPct: number;
  carryIsAccretive: boolean;
}

export interface SellPathRow {
  monthIndex: number;
  portfolio: number;
  netWorth: number;
  cumulativeTax: number;
  grossSale: number;
}

export interface SellPathResult {
  rows: SellPathRow[];
  endingNetWorth: number;
  cumulativeTax: number;
}

export interface EstateResult {
  borrowHeirsValue: number;
  sellHeirsValue: number;
  difference: number;
  lifetimeIncomeTaxAvoided: number;
  estateTaxBorrow: number;
  estateTaxSell: number;
}

export interface ProjectionResult {
  rows: MonthlyRow[];
  summary: ProjectionSummary;
  sellPath: SellPathResult;
  estate: EstateResult;
}

export interface TornadoBar {
  input: string;
  low: number;
  high: number;
  base: number;
}

/** Transient stress-test input (UI/engine only — not part of ScenarioInputs, §6.23). */
export interface StressInput {
  atMonth: number;
  globalDropPct: number;
  dropByClass?: Partial<Record<AssetClass, number>>;
}

export interface StressSnapshotResult {
  atMonth: number;
  stressedPortfolio: number;
  stressedLVC: number;
  balance: number;
  ltv: number;
  callCushionPct: number;
  marginOfSafety: number;
  maintenanceCall: boolean;
  callThresholdDropPct: number;
  cure?: { forcedSale: number; realizedTax: number };
}

export type Percentile = 'p5' | 'p25' | 'p50' | 'p75' | 'p95';

export interface MonteCarloResult {
  runs: number;
  seed: number;
  maintenanceCallProbability: number;
  netWorthPercentiles: {
    monthIndex: number;
    p5: number;
    p25: number;
    p50: number;
    p75: number;
    p95: number;
  }[];
  terminal: {
    netWorth: Record<Percentile, number>;
    loanBalance: Record<Percentile, number>;
    heirsValue: Record<Percentile, number>;
  };
}

// ───────────── defaults (§6.20, §8) ─────────────

export const GENERIC_TAX_DEFAULTS: TaxFields = {
  longTermCapGainsRatePct: 15,
  stateCapGainsRatePct: 0,
  ordinaryIncomeRatePct: 32,
  interestDeductible: false,
  estateTaxEnabled: false,
  estateExemption: 0,
  estateTaxRatePct: 0,
  useBracketedCapGains: false,
  filingStatus: 'single',
  otherTaxableIncome: 0,
  otherMagi: 0,
  indexBracketsToInflation: true,
  bracketZeroMaxOverride: null,
  bracketFifteenMaxOverride: null,
  niitThresholdOverride: null,
};

/**
 * Sensible default ScenarioInputs (§8.1). Tax fields come from the saved profile if
 * present, else generic defaults. All values are editable assumptions. The flat rate
 * path anchors on the latest SOFR observation.
 */
export function defaultScenarioInputs(
  latestSofr: number,
  taxProfile?: TaxProfile | null,
): ScenarioInputs {
  return {
    portfolioValue: 1_000_000,
    blendedAdvanceRate: 0.7,
    loanValueOverride: null,
    minimumLoanValue: 100_000,
    portfolioExpectedReturnAnnualPct: 7,
    dividendYieldAnnualPct: 0,
    dividendHandling: 'reinvest',
    contributions: [],
    collateralShocks: [],

    useBuckets: false,
    collateralBuckets: [],
    liquidationPolicy: 'schwab_liquidity',

    spreadPct: 2.9,
    dayCountBasis: 360,
    ratePath: { type: 'flat', baseRatePct: latestSofr },

    initialBalance: 0,
    drawFrequency: 'monthly',
    drawAmount: 5_000,
    drawStartMonth: 0,
    drawDurationMonths: null,
    lumpDraws: [],

    paymentMode: 'interest_only',
    fixedPayment: null,
    customPayments: [],
    extraPayments: [],

    proceedsDeployed: false,
    proceedsReturnAnnualPct: 0,

    costBasis: null,
    costBasisPct: 50,
    tax: taxProfile ?? GENERIC_TAX_DEFAULTS,
    modelForcedLiquidation: true,
    cureTargetLtv: 0.9,

    years: 10,
    inflationAnnualPct: 2.5,
  };
}
